import { jwtDecode } from 'jwt-decode';

import { ThunderboltIcon } from 'components/shared/RightSideBar/styles';
import { COLORS } from 'constants/colors';
import { POST_TYPE, PROTOCOLE } from 'constants/global';
import { USER_ROLES } from 'constants/types';

import { LOCAL_STORAGE_KEYS } from './local-storage';

const decodeToken = (token: string) => jwtDecode(token);

export const getAppRole = () => {
  const token: any = localStorage.getItem(LOCAL_STORAGE_KEYS.IdToken);
  if (!token) return USER_ROLES.NO_ROLE;

  const decodedToken: any = decodeToken(token);

  return decodedToken?.oyo_roles[0];
};

export const getAuthUser = () => {
  const token: any = localStorage.getItem(LOCAL_STORAGE_KEYS.IdToken);
  if (!token) return null;

  const user: any = decodeToken(token);

  return user;
};

export const getConsumerLegalInitials = () => {
  const token: any = localStorage.getItem(LOCAL_STORAGE_KEYS.IdToken);
  if (!token) return null;

  const { given_name, family_name }: any = decodeToken(token);

  return `${given_name.charAt(0)}${family_name.charAt(0)}`.toUpperCase();
};

export const getOnlineConsumerLegalInitials = (
  firstName: string,
  lastName: string,
) => {
  const hasFirstName = firstName?.trim().length > 0;
  const hasLastName = lastName?.trim().length > 0;

  if (hasFirstName && hasLastName) {
    return `${firstName.trim().charAt(0).toUpperCase()}${lastName.trim().charAt(0).toUpperCase()}`;
  }

  if (hasFirstName && !hasLastName) {
    return firstName.trim().charAt(0).toUpperCase();
  }

  if (!hasFirstName && hasLastName) {
    return lastName.trim().charAt(0).toUpperCase();
  }

  return '';
};

export const capitalizeAllWords = (str: string) =>
  str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

export const createHeadersWithAuthorization = (
  headers: Headers,
  contentType?: string,
) => {
  if (contentType) {
    headers.set('Content-Type', contentType);
  } else {
    headers.delete('Content-Type');
  }
  headers.set(
    'Authorization',
    `Bearer ${localStorage.getItem(LOCAL_STORAGE_KEYS.AccessToken)}`,
  );
  return headers;
};

export const isImageSmall = (
  imageElement: HTMLImageElement,
  maxWidth: number = 200,
  maxHeight: number = 200,
): boolean => {
  const { width, height } = imageElement.getBoundingClientRect();
  return width < maxWidth && height < maxHeight;
};

export function timeAgo(dateString: string): string {
  if (!dateString) {
    return '';
  }

  const now = new Date();
  const date = new Date(dateString);
  const diff = Math.round((now.getTime() - date.getTime()) / 60000);

  if (diff < 1) {
    return 'just now';
  } else if (diff < 60) {
    return `${diff} mins ago`;
  } else if (diff < 1440) {
    return `${Math.floor(diff / 60)} hrs ago`;
  } else {
    const formattedDate = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
    return formattedDate;
  }
}

export const getIconContent = (
  userRole: string,
  firstName: string,
  lastName: string,
) => {
  switch (userRole) {
    case USER_ROLES.ADMIN:
      return 'OYO';
    case USER_ROLES.PMO_ADMIN:
      return 'PMO';
    case USER_ROLES.PRODUCER:
      return <ThunderboltIcon />;
    case USER_ROLES.CONSUMER:
      return getOnlineConsumerLegalInitials(firstName, lastName);
    default:
      return '';
  }
};

export const getIconColor = (userRole: string) => {
  switch (userRole) {
    case USER_ROLES.ADMIN:
      return COLORS.Orange5;
    case USER_ROLES.PMO_ADMIN:
      return COLORS.geekBlue5;
    case USER_ROLES.PRODUCER:
      return COLORS.Yellow5;
    case USER_ROLES.CONSUMER:
      return COLORS.Green5;
    default:
      return '';
  }
};

export const getIconBorderColor = (userRole: string) => {
  switch (userRole) {
    case USER_ROLES.ADMIN:
      return COLORS.Orange8;
    case USER_ROLES.PMO_ADMIN:
      return COLORS.Blue4;
    case USER_ROLES.PRODUCER:
      return COLORS.Yellow6;
    case USER_ROLES.CONSUMER:
      return COLORS.Green6;
    default:
      return '';
  }
};

export const formatEndDate = (
  dateString: string | null | undefined,
): string | null => {
  if (!dateString) return null;

  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

export const getPostType = (post: any): POST_TYPE => {
  switch (true) {
    case !!post?.media:
      return POST_TYPE.TOPIC;
    case !!post?.meetingTitle:
      return POST_TYPE.MEET;
    case !!post?.questionTitle:
      return POST_TYPE.POLL;
    default:
      throw new Error('Invalid post structure: Unable to determine post type');
  }
};

export const isValidURL = (link: string) => {
  try {
    const url = new URL(link);
    return url.protocol === PROTOCOLE.HTTP || url.protocol == PROTOCOLE.HTTPS;
  } catch {
    return false;
  }
};

interface VoteResults {
  voters: any[];
  nonVoters: any[];
  options: { optionId: string; voters: any[] }[];
}

export const getActiveUsers = (
  voteResults: VoteResults | null,
  activeOption: string | null,
): any[] => {
  if (!voteResults) return [];

  if (activeOption === 'All') {
    return voteResults.voters;
  }

  if (activeOption === 'Non-Voters') {
    return voteResults.nonVoters;
  }

  const selectedOption = voteResults.options.find(
    (option) => option.optionId === activeOption,
  );

  return selectedOption ? selectedOption.voters : [];
};
